/**
 * Orders
 */

// React
import React, { FunctionComponent, useEffect, useState, useContext } from 'react';

// Context
import AuthContext, { AuthConsumer } from '../../authContext';

// Redux
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  updateOrderStatusActions,
  updateAllOrderStatusActions,
  actionRetrieveRubikaOrdersAction,
  getAllOrdersReportAction,
  updateOrderDeliveryTypeActions,
  changeDeliveryTypeAction,
  getPostOrdersAction,
  updatePostOrderStatusActions,
} from './actions';
import { Store } from '../../redux/reducers';

// Selector
import { createStructuredSelector } from 'reselect';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectPostOrdersCountList,
  makeSelectAllOrders,
  makeSelectAllPostOrders,
} from './selectors';

// UI
import theme from '../../theme';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Grid,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  DialogTitle,
  Menu,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiAccountOutline,
  mdiPhoneOutline,
  mdiHomeSearchOutline,
  mdiPackageVariant,
  mdiStateMachine,
  mdiCalendar,
  mdiClose,
  mdiHomeCityOutline,
  mdiPencilOutline,
  mdiOfficeBuilding,
  mdiWarehouse,
  mdiKey,
  mdiTruckDeliveryOutline,
} from '@mdi/js';

// Components
import RenderFilter from '../../utils/listDaynamicFilterHookForm';
import type { Filter } from '../../utils/listDaynamicFilter';
import List from './postOrderList';
import Pagination from '@material-ui/lab/Pagination';
import Can from '../../components/Can';
import CSV from './csv';
import ExelExport from './components/exelExport';

// Helper
import {
  removeEmptyValueObject,
  translateOrderStatus,
  checkMatchMedia,
  translateSendDeliveryType,
  translatePostOrderStatus,
  flattenObject,
} from '../../utils/helpers';
//request
import { GetPostOrdersRequestBuilder } from 'client-sdk/src/orders/orders';
import Client from 'client-sdk/src/client';
import handleError from '../../utils/handleError';
// Type
import {
  Props,
  SelectorProps,
  Payload,
  Status,
  cancelPayload,
  PayloadAllUpdate,
  PayloadChangeDeliveryType,
  PostOrderStatusType,
} from './interfaces';
import { Link } from 'react-router-dom';
import useQuery from '../../utils/hook/useQuery';
import { rulesOrderStatus } from '../../utils/rulesOrderStatus';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },
    input: {
      display: 'flex',
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      marginRight: '10px',
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    textArea: {
      width: '100%',
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      marginTop: '30px',
      top: '47px',
      right: 0,
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    typeSection: {
      textAlign: 'center',
      marginBottom: theme.spacing(2),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    btnBox: {
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
  })
);

type Inputs = {
  'customerNationalCode.contains': string;
};

const Orders: FunctionComponent<Props> = (props: Props) => {
  const authContext = useContext(AuthContext);
  const token = localStorage.getItem('token') || '';
  const [filters, setFilters] = useState([] as Filter[]);
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 100,
  } as any);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmCancel, setOpenConfirmCancel] = useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const [openUpdatePostOrderStatus, setOpenUpdatePostOrderStatus] = useState(
    false as boolean
  );
  const [confirmDialog, setConfirmDialog] = useState(false as boolean);
  const [sendType, setSendType] = useState<null | string>(null);
  const [id, setId] = useState(-1 as number);
  const [postOrderId, setPostOrderId] = useState(-1 as number);
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);
  const [previewPostOrderStatus, setPreviewPostOrderStatus] = useState(
    'DRAFT' as PostOrderStatusType
  );
  const [changeType, setChangeType] = useState(false as boolean);
  const [lastUpdate, setLastUpdate] = useState(
    '2019-07-02T17:15:03+04:30' as string
  );
  const [postOrders, setPostOrders] = useState([]);
  const [cancelDescription, setCancelDescription] = useState('' as string);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [openAlertWarning, setOpenAlertWarning] = useState(false as boolean);

  const selectItem = (id: string, status?: string) => {
    const selectedIndex = selected.indexOf(id);
    if (selectedIndex > -1) {
      setSelectedStatus(selectedStatus.filter(el => el != status));
      setSelected(selected.filter(el => el != id));
    } else {
      setSelectedStatus([...selectedStatus, status]);
      setSelected([...selected, id]);
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = props.ordersList.map((n: any) => n.id);
      const newSelectedStatus = props.ordersList.map((n: any) => n.status);
      setSelected(newSelecteds);
      setSelectedStatus(newSelectedStatus);
      return;
    }
    setSelectedStatus([]);
    setSelected([]);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const groupStatusChange = (status: string) => {
    const checkList = new Array(...new Set(selectedStatus));
    if (checkList.length > 1) {
      setOpenAlertWarning(true);
      return;
    }
    const payload = {
      ids: selected,
      status,
      pagination: filtersValues,
    };
    props.updateAllOrderStatusDispatch(payload);
  };

  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش‌دهنده:',
      'customerName.equals': 'نام سفارش‌دهنده:',
      'customerName.contais': 'نام سفارش‌دهنده:',
      centerName: 'انبار:',
      'centerName.equals': 'انبار:',
      'centerName.contains': 'انبار:',
      id: 'شناسه مرسوله:',
      'id.equals': 'شناسه مرسوله:',
      'id.contains': 'شناسه مرسوله:',
      orderId: 'شناسه سفارش:',
      'orderId.equals': 'شناسه سفارش:',
      'orderId.contains': 'شناسه سفارش:',
      deliveryDate: 'تاریخ تحویل',
      'deliveryDate.equals': 'تاریخ تحویل:',
      'deliveryDate.greaterThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.greaterThan': 'تاریخ تحویل:',
      'deliveryDate.lessThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.lessThan': 'تاریخ تحویل:',
      packagedDate: 'تاریخ تحویل به لجستیک:',
      'packagedDate.equals': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThan': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThan': 'تاریخ تحویل به لجستیک:',
      status: 'وضعیت سفارش:',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
      partnerName: 'شرکت:',
      'partnerName.equals': 'شرکت:',
      'partnerName.contains': 'شرکت:',
      weight: 'وزن:',
      'weight.equals': 'وزن:',
      'weight.contains': 'وزن:',
      length: 'ابعاد:',
      'length.equals': 'ابعاد:',
      'length.contains': 'ابعاد:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };
  const getPostOrdersCsv = async (payload: any) => {
    try {
      const makeRequest = new GetPostOrdersRequestBuilder()
        .params(flattenObject({ ...payload, sort: 'id,desc' }))
        .build();
      const client = new Client(token);
      const { data } = await client.execute(makeRequest);
      setPostOrders(data);
    } catch (err) {
      handleError(err.response);
    }
  };

  // Bad Bad Bad.. :(
  props.ordersList[0]?.created &&
    props.ordersList[0]?.created > lastUpdate &&
    setLastUpdate(props.ordersList[0]?.created);

  useEffect(() => {
    getOrders();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };

  const handleOpenChangeType = (id: number) => {
    setChangeType(true);
    setId(id);
  };

  const handleChangeSendType = async () => {
    try {
      props.changeDeliverTypeDispatch({
        id,
        body: sendType?.toString(),
        filters: filtersValues,
        callback: handleCallback,
      });
    } catch (err) {
      //
    }
  };

  const handleCloseConfirmDialog = () => {
    setSendType(null);
    setConfirmDialog(false);
  };

  const handleSelectType = (type: string) => {
    setSendType(type);
    setConfirmDialog(true);
  };

  const handleCallback = () => {
    handleCloseConfirmDialog();
    handleCloseChangeType();
  };

  const handleCloseChangeType = () => {
    setChangeType(false);
  };

  const getOrders = () => {
    const { page, size } = filtersValues;
    props.ordersDispatch({
      ...filtersValues,
      page,
      size,
    });
  };

  const setupFilters = () => {
    Can({
      role: authContext.user.role,
      perform: 'orders:filtersByCenterId',
      yes: () => {
        setFilters([
          {
            type: 'input',
            id: 'customerName.contains',
            label: 'نام سفارش‌دهنده',
            icon: mdiAccountOutline,
          },
          {
            type: 'input',
            id: 'customerPhoneNumber.contains',
            label: 'شماره همراه سفارش‌دهنده',
            icon: mdiPhoneOutline,
          },
          {
            type: 'input',
            id: 'id.equals',
            label: 'شناسه سفارش',
            icon: mdiPackageVariant,
          },
          {
            type: 'input',
            id: 'trackingCode.contains',
            label: 'کد پیگیری',
            icon: mdiKey,
          },
          {
            type: 'input',
            id: 'customerNationalCode.contains',
            label: 'کد  ملی',
            icon: mdiPackageVariant,
          },

          {
            type: 'input',
            id: 'centerName.contains',
            label: 'نام مرکز',
            icon: mdiHomeSearchOutline,
          },

          {
            type: 'input',
            id: 'centerProvince.contains',
            label: 'نام  استان',
            icon: mdiHomeCityOutline,
          },
          {
            type: 'input',
            id: 'centerCity.contains',
            label: 'نام  شهر',
            icon: mdiHomeCityOutline,
          },

          {
            type: 'date-picker-time',
            id: 'created.greaterThanOrEqual',
            label: 'تاریخ ثبت سفارش از ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker-time',
            id: 'created.lessThanOrEqual',
            label: 'تا تاریخ ثبت سفارش  ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker',
            id: 'deliveryDate.greaterThanOrEqual',
            label: 'تاریخ تحویل از ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker',
            id: 'deliveryDate.lessThanOrEqual',
            label: '  تا تاریخ تحویل ',
            icon: mdiCalendar,
          },
          {
            type: 'checkbox',
            id: 'status.in',
            label: 'وضعیت سفارش',
            icon: mdiStateMachine,
            data: [
              'ORDERED',
              'DELIVERED_TO_POST',
              'DELIVERED_TO_CENTER',
              'SUPPLIED_IN_CENTER',
              'PACKAGED',
              'BUNDLED',
              'LOADED',
              'ARRIVED',
              'DELIVERED',
              'NOT_DELIVERED',
              'RETURNED',
              'CANCELLED',
            ],
            translateType: 'order',
          },
        ]);
      },
      no: () => {
        setFilters([
          {
            type: 'input',
            id: 'customerName.contains',
            label: 'نام سفارش‌دهنده',
            icon: mdiAccountOutline,
          },
          {
            type: 'input',
            id: 'partnerName.contains',
            label: 'شرکت',
            icon: mdiOfficeBuilding,
          },
          {
            type: 'input',
            id: 'id.equals',
            label: 'شناسه مرسوله',
            icon: mdiPackageVariant,
          },
          {
            type: 'input',
            id: 'orderId.equals',
            label: 'شناسه سفارش',
            icon: mdiPackageVariant,
          },
          {
            type: 'input',
            id: 'trackingCode.equals',
            label: 'کد پیگیری',
            icon: mdiKey,
          },
          // {
          //   type: 'input',
          //   id: 'trackingCode.contains',
          //   label: 'شناسه پیگیری',
          // },

          {
            type: 'input',
            id: 'centerName.contains',
            label: 'مبدا (مرکز)',
            icon: mdiWarehouse,
          },
          {
            type: 'input',
            id: 'fulfillmentWarehouseName.contains',
            label: 'مبدا (انبار)',
            icon: mdiWarehouse,
          },
          // {
          //   type: 'input',
          //   id: 'centerProvince.contains',
          //   label: 'نام  استان',
          // },
          // {
          //   type: 'input',
          //   id: 'centerCity.contains',
          //   label: 'نام  شهر',
          // },

          // {
          //   type: 'date-picker-time',
          //   id: 'created.greaterThanOrEqual',
          //   label: 'تاریخ ثبت سفارش از ',

          // }, {
          //   type: 'date-picker-time',
          //   id: 'created.lessThanOrEqual',
          //   label: 'تا تاریخ ثبت سفارش  ',

          // },
          {
            type: 'date-picker',
            id: 'orderDeliveryDate.greaterThanOrEqual',
            label: 'تاریخ تحویل از ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker',
            id: 'orderDeliveryDate.lessThanOrEqual',
            label: '  تا تاریخ تحویل ',
            icon: mdiCalendar,
          },
          {
            type: 'checkbox',
            id: 'orderStatus.in',
            label: 'وضعیت سفارش',
            icon: mdiStateMachine,
            data: [
              'ORDERED',
              'DELIVERED_TO_POST',
              'DELIVERED_TO_CENTER',
              'SUPPLIED_IN_CENTER',
              'PACKAGED',
              'BUNDLED',
              'LOADED',
              'ARRIVED',
              'DELIVERED',
              'NOT_DELIVERED',
              'RETURNED',
              'CANCELLED',
            ],
            translateType: 'order',
          },
          {
            type: 'date-picker-time',
            id: 'updated.greaterThanOrEqual',
            label: 'تاریخ ارسال به پست از ',
            icon: mdiCalendar,
          },
          {
            type: 'date-picker-time',
            id: 'updated.lessThanOrEqual',
            label: '  تا تاریخ ارسال به پست ',
            icon: mdiCalendar,
          },
        ]);
      },
    });
  };
  let headCells = [];
  Can({
    role: authContext.user.role,
    perform: 'orders:list:centerName',
    yes: () => {
      headCells = [
        {
          id: 'customerName',
          label: 'نام سفارش‌دهنده',
          type: 'input',
        },
        { id: 'partnerName', label: 'شرکت', type: 'input' },
        { id: 'id', label: 'کد سفارش', type: 'input' },
        { id: 'centerName', label: 'مرکز سفارش', type: 'input' },
        {
          id: 'deliveryDate',
          label: 'زمان تحویل',
          type: 'date-picker',
        },
        {
          id: 'status',
          label: 'وضعیت سفارش',
          type: 'checkbox',
          data: [
            'ORDERED',
            'DELIVERED_TO_POST',
            'DELIVERED_TO_CENTER',
            'SUPPLIED_IN_CENTER',
            'PACKAGED',
            'BUNDLED',
            'LOADED',
            'ARRIVED',
            'DELIVERED',
            'NOT_DELIVERED',
            'RETURNED',
            'CANCELLED',
          ],
          translateType: 'Order',
        },
      ];
    },
    no: () => {
      headCells = [
        { id: 'orderId', label: 'شناسه سفارش', type: 'input' },
        { id: 'id', label: 'شناسه مرسوله', type: 'input' },
        { id: 'partnerName', label: 'شرکت', type: 'input' },
        {
          id: 'customerName',
          label: 'سفارش‌دهنده',
          type: 'input',
        },
        {
          id: 'orderDeliveryDate',
          label: 'تاریخ تحویل',
          type: 'date-picker',
        },
        {
          id: 'orderStatus',
          label: 'وضعیت سفارش',
          type: 'checkbox',
          data: [
            'ORDERED',
            'DELIVERED_TO_POST',
            'DELIVERED_TO_CENTER',
            'SUPPLIED_IN_CENTER',
            'PACKAGED',
            'BUNDLED',
            'LOADED',
            'ARRIVED',
            'DELIVERED',
            'NOT_DELIVERED',
            'RETURNED',
            'CANCELLED',
          ],
          translateType: 'Order',
        },
        {
          id: 'postOrderStatus',
          label: 'وضعیت مرسوله',
          type: 'checkbox',
          data: [
            'ORDERED',
            'DELIVERED_TO_POST',
            'DELIVERED_TO_CENTER',
            'SUPPLIED_IN_CENTER',
            'PACKAGED',
            'BUNDLED',
            'LOADED',
            'ARRIVED',
            'DELIVERED',
            'NOT_DELIVERED',
            'RETURNED',
            'CANCELLED',
          ],
          translateType: 'Order',
        },
      ];
    },
  });
  const head = { headCells };
  const handleCancelOrder = async () => {
    await props.updateOrderDeliveryTypeDispatch({
      id,
      body: 'COURIER',
      pagination: { ...filtersValues, deliveryType: { equals: 'POST' } },
    });
    setOpenConfirmCancel(false);
  };

  const changeStatusOrder = () => {
    props.updateOrderStatusDispatch({
      id,
      body: { status: previewState, description: cancelDescription },
      pagination: filtersValues,
      callback: () => {
        getOrders();
      },
    });
    setOpenConfirmUpdateState(false);
    setCancelDescription('');
  };

  const changeStatusPostOrder = () => {
    props.updatePostOrderStatusDispatch({
      id: postOrderId,
      body: { status: previewPostOrderStatus.replace(/'/g, '"') },
      pagination: filtersValues,
    });
    setOpenUpdatePostOrderStatus(false);
  };

  const changeStatusOrderWhitId = (data: any) => {
    const { ids, status, description } = data;
    props.updateAllOrderStatusDispatch({
      ids,
      status,
      description,
      pagination: filtersValues,
    });

    // props.updateOrderStatusDispatch({
    //   id,
    //   body,
    //   pagination: filtersValues,
    // });
    setOpenConfirmUpdateState(false);
  };

  const handleOpenConfirmCancele = (id: number) => {
    setOpenConfirmCancel(true);
    setId(id);
  };
  const handleOpenConfirmUpdateState = (data: { id: number; body: Status }) => {
    setOpenConfirmUpdateState(true);
    setId(data.id);
    setPreviewState(data.body);
  };

  const handleOpenUpdatePostOrderStatus = (data: {
    id: number;
    body: Status;
  }) => {
    setOpenUpdatePostOrderStatus(true);
    setPostOrderId(data.id);
    setPreviewPostOrderStatus(data.body);
  };

  const handleClosePostStatus = () => {
    setOpenConfirmCancel(false);
    setOpenUpdatePostOrderStatus(false);
    setOpenSize(false);
  };

  const handleClose = () => {
    setOpenConfirmCancel(false);
    setOpenConfirmUpdateState(false);
    setOpenSize(false);
    setCancelDescription('');
  };

  const findOrderStatus = (id: number) => {
    const status = props.ordersList.find(
      order => order.orderId === id
    )?.orderStatus;
    return status && translateOrderStatus(status);
  };

  const findPostOrderStatus = (id: number) => {
    const status = props.ordersList.find(
      order => order.id === id
    )?.postOrderStatus;
    return status && translatePostOrderStatus(status);
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };

  return (
    <AuthConsumer>
      {({ user }) => (
        <div className={classes.root}>
          <Grid container spacing={5}>
            <Grid container item xs={12} sm={6} alignItems="center">
              <Typography
                variant="h4"
                component="div"
                style={{ marginLeft: '20px', fontSize: '18px' }}
              >
                {`لیست مرسوله‌های پست`}
              </Typography>

              {/* <div className={classes.subtitle}>
                &nbsp; &nbsp;- &nbsp; &nbsp;
                {`آخرین سفارش  دریافتی در `}
                {props.ordersList[0]?.created &&
                  jMoment(lastUpdate, 'YYYY/M/D HH:mm:ss')
                    .locale('fa')
                    .format(`jDD jMMMM jYY ساعت HH:mm`)}
              </div> */}
            </Grid>
            <Grid item xs={12} sm={6}>
              <RenderFilter
                filters={filters}
                onFilter={(filtervalues: any) => {
                  setFiltersValues({
                    ...removeEmptyValueObject(filtervalues),
                    page: 0,
                    size: 100,
                  });
                }}
                mainFiltersCount={0}
                baseFilter={{
                  type: 'input',
                  id: 'orderId.equals',
                  placeholder: 'شناسه سفارش',
                }}
                filterValues={filtersValues}
              />
            </Grid>
            <Grid xs={12} sm={6} item container alignItems="center">
              {props.loading ? (
                ''
              ) : (
                <>
                  {props.ordersList.length > 0 && (
                    <>
                      <div
                        className={classes.subtitle}
                      >{`تعداد کل سفارش‌ها:`}</div>
                      <div>
                        &nbsp;
                        <b>{`${props.count}`}</b>
                      </div>
                    </>
                  )}
                </>
              )}
            </Grid>
            <Grid
              container
              item
              xs={12}
              md={6}
              direction="row-reverse"
              alignItems="center"
            >
              <div className={classes.btnBox}>
                <Can
                  role={user.role}
                  perform="orders:btnGetNewOrders"
                  yes={() => (
                    <>
                      <Button
                        variant="contained"
                        color="secondary"
                        size={
                          checkMatchMedia('(max-width: 1024px)')
                            ? 'small'
                            : 'large'
                        }
                        onClick={() =>
                          props.actionRetrieveRubikaOrdersDispatch({})
                        }
                      >{`دریافت سفارش جدید`}</Button>
                    </>
                  )}
                />
                {(filtersValues['updated.lessThanOrEqual'] ||
                  filtersValues['updated.greaterThanOrEqual']) && (
                    <Link
                      style={{ textDecoration: 'none' }}
                      to={`/post-orders/post-receipt?filters=${JSON.stringify(
                        filtersValues
                      )}`}
                      target="_blank"
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        size={
                          checkMatchMedia('(max-width: 1024px)')
                            ? 'small'
                            : 'large'
                        }
                      >
                        {`چاپ رسید پستی`}
                      </Button>
                    </Link>
                  )}
                <Can
                  role={user.role}
                  perform="/post-orders-queue"
                  yes={() => (
                    <Link
                      style={{ textDecoration: 'none', margin: '0 5px' }}
                      to="/post-orders-queue"
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        size={
                          checkMatchMedia('(max-width: 1024px)')
                            ? 'small'
                            : 'large'
                        }
                      >
                        {`صف سفارش های پست`}
                      </Button>
                    </Link>
                  )}
                />
                <Can
                  role={user.role}
                  perform="orders:downloadReportCSV"
                  yes={() => (
                    <>
                      <CSV
                        ordersDispatch={getPostOrdersCsv}
                        data={postOrders}
                        filtersValues={filtersValues}
                        loading={props.loading}
                      />
                      <ExelExport
                        data={postOrders}
                        loading={props.loading}
                        filtersValues={filtersValues}
                        handleRequest={getPostOrdersCsv}
                      />
                    </>
                  )}
                />
                {/* {selected.length >= 1 && (
                  <React.Fragment>
                    <Button
                      style={{marginLeft: '5px'}}
                      variant="contained"
                      color="primary"
                      onClick={handleOpenMenu}
                      aria-controls="basic-menu"
                      aria-haspopup="true"
                      aria-expanded={openMenu ? 'true' : undefined}
                    >
                      تغییر وضعیت گروهی
                    </Button>
                    <Menu
                      id="group-status"
                      aria-labelledby="group-status"
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={handleCloseMenu}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      // transformOrigin={{
                      //   vertical: 'top',
                      //   horizontal: 'center',
                      // }}
                    >
                      {rulesOrderStatus[selectedStatus[0]]?.map(
                        (item: any, index: number) => (
                          <MenuItem onClick={() => groupStatusChange(item)}>
                            {translateOrderStatus(item)}
                          </MenuItem>
                        )
                      )}
                    </Menu>
                  </React.Fragment>
                )} */}
              </div>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {props.loading ? (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {props.ordersList.length > 0 ? (
                    <List
                      data={props.ordersList}
                      handleOpenChangeType={handleOpenChangeType}
                      handleConfirmCanceleBox={handleOpenConfirmCancele}
                      changeStatusOrderWhitId={changeStatusOrderWhitId}
                      handleOpenUpdatePostOrderStatus={
                        handleOpenUpdatePostOrderStatus
                      }
                      handleOpenConfirmUpdateState={
                        handleOpenConfirmUpdateState
                      }
                      selectItem={selectItem}
                      selectedItems={selected}
                      selectAll={handleSelectAllClick}
                      head={head}
                      filterVal={filtersValues}
                      onFilterValue={(filtervalues: any) => {
                        setFiltersValues({
                          ...removeEmptyValueObject(filtervalues),
                          ...filtersValues,
                          page: 0,
                          size: 10,
                        });
                      }}
                      removeFilterHandler={removeFilterHandler}
                      filterLabelTranslator={filterTranslator}
                    />
                  ) : (
                    <Typography variant="h3" component="div">
                      سفارشی موجود نیست
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {props.ordersList.length > 0 && (
                <FormControl
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                    {' '}
                    تعداد نمایش در هر صفحه:
                  </div>
                  <Select
                    open={openSize}
                    onClose={handleClose}
                    onOpen={() => setOpenSize(true)}
                    value={filtersValues.size}
                    onChange={handleChangeSizePage}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid xs={12} sm={6} container item direction="row-reverse">
              {props.ordersList.length > 0 && (
                <Pagination
                  className={classes.pagination}
                  count={Math.ceil(props.count / filtersValues.size)}
                  color="secondary"
                  page={filtersValues.page + 1}
                  onChange={handleChangePage}
                />
              )}
            </Grid>
          </Grid>

          <Dialog
            open={openConfirmCancel}
            fullWidth
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.cancelIcon}>
                <Icon
                  path={mdiClose}
                  size={4}
                  color={theme.palette.error.main}
                />
              </div>
              <div
                className={classes.cancelTitle}
              >{`از حذف سفارش با شناسه ${id} مطمئن هستید؟`}</div>
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCancelOrder}
                    size="large"
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>

          {/* <Dialog
            fullWidth
            open={openConfirmUpdateState}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClose}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.editIcon}>
                <Icon
                  path={mdiPencilOutline}
                  size={4}
                  color={theme.palette.blue.main}
                />
              </div>
              <div className={classes.cancelTitle}>
                {`از تغییر تگ وضعیت سفارش
                با شناسه  ${id} از
 "${id > -1 && findOrderStatus(id)}"
 به
" ${translateOrderStatus(previewState.replace(/"/g, '') as Status)}"

 
 مطمئن هستید؟`}
              </div>
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={changeStatusOrder}
                    size="large"
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog> */}
          <Dialog
            fullWidth
            open={openAlertWarning}
            onClose={() => setOpenAlertWarning(false)}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogTitle>امکان تغییر وضعیت سفارش وجود ندارد </DialogTitle>
            <DialogContent>
              وضعیت سفارش آیتم‌های انتخاب شده باید یکی باشد
            </DialogContent>
            <DialogActions>
              <Button
                autoFocus
                onClick={() => setOpenAlertWarning(false)}
                variant="contained"
                color="primary"
              >
                {'متوجه شدم'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            open={openConfirmUpdateState}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClose ? (
                  <IconButton aria-label="close" onClick={handleClose}>
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.cancelIcon}>
                <Icon
                  path={mdiClose}
                  size={4}
                  color={theme.palette.error.main}
                />
              </div>
              <div className={classes.cancelTitle}>
                {`از تغییر تگ وضعیت سفارش
                با شناسه  ${id} از
 "${id > -1 && findOrderStatus(id)}"
 به
" ${translateOrderStatus(previewState.replace(/"/g, '') as Status)}"

 
 مطمئن هستید؟`}
              </div>
              <TextField
                fullWidth
                id="description"
                name="description"
                className={classes.textArea}
                placeholder={`علت عدم تحویل...`}
                value={cancelDescription}
                onChange={e => setCancelDescription(e.target.value)}
              />
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClose}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={changeStatusOrder}
                    size="large"
                    disabled={!(cancelDescription.length > 0)}
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            open={openUpdatePostOrderStatus}
            onClose={handleClosePostStatus}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                {handleClosePostStatus ? (
                  <IconButton
                    aria-label="close"
                    // className={classes.closeButton}
                    onClick={handleClosePostStatus}
                  >
                    <Icon path={mdiClose} size={1} />
                  </IconButton>
                ) : null}
              </div>
              <div className={classes.editIcon}>
                <Icon
                  path={mdiPencilOutline}
                  size={4}
                  color={theme.palette.blue.main}
                />
              </div>
              <div className={classes.cancelTitle}>
                {`از تغییر تگ وضعیت مرسوله
                با شناسه  ${postOrderId} از
                  "${postOrderId > -1 && findPostOrderStatus(postOrderId)}"
                                به
                      " ${translatePostOrderStatus(
                  previewPostOrderStatus.replace(
                    /"/g,
                    ''
                  ) as PostOrderStatusType
                )}"
 مطمئن هستید؟`}
              </div>
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleClosePostStatus}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {`خیر`}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={changeStatusPostOrder}
                    size="large"
                  >
                    {`بله`}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>

          <Dialog
            open={changeType}
            fullWidth
            onClose={handleCloseChangeType}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                <IconButton aria-label="close" onClick={handleCloseChangeType}>
                  <Icon path={mdiClose} size={1} />
                </IconButton>
              </div>
              <div className={classes.editIcon}>
                <Icon
                  path={mdiTruckDeliveryOutline}
                  size={4}
                  color={theme.palette.blue.main}
                />
              </div>

              <div className={classes.cancelTitle}>انتخاب نوع حمل</div>

              <div className={classes.typeSection}>
                <Button
                  variant="contained"
                  onClick={handleSelectType.bind(this, 'VIP')}
                  size="large"
                >
                  ارسال ویژه
                </Button>
                <Button
                  variant="contained"
                  style={{ marginRight: '1rem' }}
                  onClick={handleSelectType.bind(this, 'DELIVER_IN_RONAD')}
                  size="large"
                >
                  تحویل در انبار
                </Button>
                <Button
                  variant="contained"
                  style={{ marginRight: '1rem' }}
                  onClick={handleSelectType.bind(this, 'COURIER')}
                  size="large"
                >
                  لجستیک
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            fullWidth
            open={confirmDialog}
            onClose={handleCloseConfirmDialog}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogContent>
              <div
                style={{
                  position: 'absolute',
                  left: '17px',
                  zIndex: 10,
                }}
              >
                <IconButton
                  aria-label="close"
                  onClick={handleCloseConfirmDialog}
                >
                  <Icon path={mdiClose} size={1} />
                </IconButton>
              </div>
              <div className={classes.cancelTitle}>
                آیا از انتخاب {sendType && translateSendDeliveryType(sendType)}{' '}
                برای شیوه ارسال سفارش {id} مطمئنید؟
              </div>
            </DialogContent>
            <DialogActions className={classes.actionsBox}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Button
                    fullWidth
                    autoFocus
                    onClick={handleCloseConfirmDialog}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {'خیر'}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleChangeSendType}
                    size="large"
                  >
                    {'بله'}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </AuthConsumer>
  );
};

const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
  ordersList: makeSelectAllPostOrders(),
  allOrders: makeSelectAllOrders(),
  count: makeSelectPostOrdersCountList(),
  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    ordersDispatch: (payload: Payload) =>
      dispatch(getPostOrdersAction(payload)),
    allOrdersDispatch: (payload: Payload) =>
      dispatch(getAllOrdersReportAction(payload)),
    updateOrderStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updateOrderStatusActions(payload)),

    updatePostOrderStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updatePostOrderStatusActions(payload)),
    updateAllOrderStatusDispatch: (payload: PayloadAllUpdate) =>
      dispatch(updateAllOrderStatusActions(payload)),
    actionRetrieveRubikaOrdersDispatch: (payload: {
      date?: String;
      from?: number;
      to?: number;
    }) => dispatch(actionRetrieveRubikaOrdersAction(payload)),
    updateOrderDeliveryTypeDispatch: (payload: cancelPayload) =>
      dispatch(updateOrderDeliveryTypeActions(payload)),
    changeDeliverTypeDispatch: (payload: PayloadChangeDeliveryType) =>
      dispatch(changeDeliveryTypeAction(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(Orders);
